import React, { useState, useEffect } from 'react';

// Styles //
import "./checkboxSquare.scss";
import ToastAdvertiser from '../toast-advertiser/toastAdvertiser';
import warningSvg from "../../../../src/assets/images/icons/icon-alert.svg";

interface CheckboxSquareProps {
   setInputValue: (nombreId: string, course: string, type: string) => void;
   nombreId: string;
   type: string;
   options?: any[]; // Formato: ["Etiqueta/Duración/Sector"]
   values?: string[];
   maxCharacters?: number; // Límite de selección
   counterText?: (selected: number, max: number) => string; // Texto dinámico para el contador
   sectorDescription?: string;
   noOptionsText?: string;
}

const sectorDescriptions: { [key: string]: string } = {
   "Agrícola": "Desarrolla competencias en producción y cuidado de cultivos, acercándote a conceptos como la tecnificación, logística, manejo de herramientas del agro y desarrollo del sector en la Región.",
   "Industrial": "Encuentra herramientas para mejorar la productividad en procesos de producción, y explora contenidos relacionados con logística, procesos administrativos y eficiencia operativa.",
   "Minero y de Construcción": "Accede a formaciones cortas que te permitirán introducirte al sector minero y de construcción, así como explorar herramientas para su ejercicio, bajo el concepto de la sostenibilidad ambiental.",
   "Turismo": "Desarrolla competencias en la promoción y fortalecimiento del turismo por medio de formaciones en servicio al cliente, inglés técnico, ventas, técnicas de negociación, entre otras."
};

const CheckboxSquare = (props: CheckboxSquareProps) => {
   const {
      nombreId,
      type,
      options = [],
      setInputValue,
      values = [],
      maxCharacters = 3,
      counterText = (selected, max) => `${selected} de ${max} cursos seleccionados`,
      noOptionsText = "En este sector económico actualmente no hay cursos disponibles. Revisa nuevamente la oferta y selecciona un curso en otro sector.",
   } = props;

   const [checkboxSelected, setCheckboxSelected] = useState<string[]>(values);
   const [showToast, setShowToast] = useState(false);

   // Extraer el sector desde la primera opción, en la posición 2
   const sector = options?.length > 0 ? options[0]?.split("/")?.[2] || "" : "";
   const sectorDescription = sectorDescriptions[sector] || "";

   const handleChange = (option: string, isSelected: boolean) => {
      const newSelected = isSelected
         ? [...checkboxSelected, option]
         : checkboxSelected.filter(item => item !== option);
      if (newSelected.length > maxCharacters) {
         setShowToast(true);
         return;
      }
      setCheckboxSelected(newSelected);
      const courses = newSelected.map(selectedOption => selectedOption.split("/")[0]);
      setInputValue(nombreId, courses.join(","), type);
   };

   useEffect(() => {
      if (showToast) {
         const timer = setTimeout(() => {
            setShowToast(false);
         }, 3000);
         return () => clearTimeout(timer);
      }
   }, [showToast]);

   const [showModal, setShowModal] = useState(false);

   const handleCloseModal = () => {
      setShowModal(false);
   };

   return (
      <>
         {sectorDescription && (
            <div className="a-checkbox-square__sector">
               <p> <strong>Sector {sector} :</strong> {sectorDescription}</p>
            </div>
         )}

         {/* Contador y Mensaje */}
         {options?.length > 0 && checkboxSelected.length > 0 && (
            <>
               {/* Contador */}
               <div className="a-checkbox-square__counter">
                  {checkboxSelected.length >= 2 && (
                     <span className="a-checkbox-square__icon">✔</span>
                  )}
                  <span className="a-checkbox-square__text">
                     {counterText(checkboxSelected.length, maxCharacters)}
                  </span>
               </div>

               {/* Mensaje de advertencia */}
               {checkboxSelected.length < 2 && (
                  <p className="a-checkbox-square__warning">
                     Debes seleccionar dos cursos para continuar.
                  </p>
               )}
            </>
         )}

         {/* Solo mostrar el bloque de opciones si hay elementos en `options` */}
         {options?.length > 1 && (
            <div className="a-checkbox-square">
               {showToast && (
                  <ToastAdvertiser
                     imgSrc={warningSvg}
                     text="Seleccionaste el número máximo de cursos permitidos."
                     imgBgColor="#FF0000"
                  />
               )}
               {options?.map((option, index) => {
                  const [label, duration, optionSector] = option.split("/");
                  const isSelected = checkboxSelected.includes(option);

                  return (
                     <div className="a-checkbox-square__card" key={index}>
                        <p className="a-checkbox-square__modality">{optionSector}</p>
                        <label htmlFor={`${nombreId}_${index}`} className="a-checkbox-square__label">
                           <input
                              type="checkbox"
                              id={`${nombreId}_${index}`}
                              name={nombreId}
                              value={option}
                              checked={isSelected}
                              onChange={() => handleChange(option, !isSelected)}
                           />
                           <span>{label}</span>
                        </label>
                        <p className="a-checkbox-square__duration">
                           {duration ? `Duración: ${duration} horas` : "Duración no especificada"}
                        </p>
                     </div>
                  );
               })}
            </div>
         )}

         {options?.length < 2 && (
            <>
               <div className="a-checkbox-square__no-options" onClick={() => setShowModal(true)}>
                  {noOptionsText}
               </div>
               {showModal && (
                  <div className="a-checkbox-square__overlay">
                     <div className="a-checkbox-square__modal">
                        <h2>{noOptionsText}</h2>
                        <button className="a-checkbox-square__modal-close" onClick={handleCloseModal}>
                           x
                        </button>
                     </div>
                  </div>
               )}
            </>
         )}
      </>
   );
};

export default CheckboxSquare;
